/**
 * CART CUTOFF / STALE DELIVERY DATE CHECKER
 * Built for archiesonline.com — Ella theme (BKTheme) + Easify Date Picker
 *
 * WHAT IT DOES:
 * - Reads the "Delivery Date" line-item property Easify attaches to cart items
 * - Compares it to "now" using that product's own cutoff time
 * - If the date is a past calendar day, OR it's today's date but the cutoff
 *   time has already passed, it removes that line item from the cart
 *   automatically (and removes any linked add-on item, e.g. candles) and
 *   shows a message to the customer.
 *
 * CONFIRMED FROM YOUR CART.JS:
 * - Property name is exactly: "Delivery Date"
 * - Date format is: DD/MM/YYYY  (e.g. "27/07/2026")
 * - Add-on products (like the candle) are linked via "_sg-addon-id" matching
 *   the main product's "_sg-master-id" — these get removed together.
 *
 * WHERE TO ADD IT:
 * Theme editor > Edit code > Assets > Add a new asset > paste this in as
 * cart-cutoff-check.js
 * Then in theme.liquid, right before </body>, add:
 *   {{ 'cart-cutoff-check.js' | asset_url | script_tag }}
 */

const DATE_PROPERTY_NAME = "Delivery Date";

// ============ CUTOFF TIME ============
// All products use the same same-day cutoff: 19:00.
const DEFAULT_CUTOFF = "19:00";

function getCutoffTimeForHandle(handle) {
  return DEFAULT_CUTOFF;
}

// ============ DATE PARSING (matches your confirmed DD/MM/YYYY format) ============
function parseDeliveryDate(dateString) {
  const match = String(dateString).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const year = parseInt(match[3], 10);
  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? null : date;
}

function isSameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isDeliveryDateExpired(deliveryDate, cutoffTimeStr) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deliveryDay = new Date(
    deliveryDate.getFullYear(),
    deliveryDate.getMonth(),
    deliveryDate.getDate()
  );

  // Past calendar day — always expired
  if (deliveryDay < today) return true;

  // Today's date — check against this product's cutoff time
  if (isSameCalendarDay(deliveryDay, today)) {
    const [cutH, cutM] = cutoffTimeStr.split(":").map(Number);
    const cutoffMoment = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      cutH,
      cutM,
      0
    );
    return now > cutoffMoment;
  }

  // Future date — still valid
  return false;
}

async function checkCartForExpiredDates() {
  let cart;
  try {
    const res = await fetch("/cart.js");
    cart = await res.json();
  } catch (e) {
    console.error("Cutoff check: failed to load cart", e);
    return;
  }

  const keysToRemove = new Set();
  const removedTitles = [];

  // Build a lookup of master-id -> item, and addon-id -> item,
  // so removing a cake also removes its linked candle/add-on.
  const masterIdToKey = {};
  for (const item of cart.items) {
    const props = item.properties || {};
    if (props["_sg-master-id"]) {
      masterIdToKey[String(props["_sg-master-id"])] = item.key;
    }
  }

  for (const item of cart.items) {
    const props = item.properties || {};
    const rawDate = props[DATE_PROPERTY_NAME];
    if (!rawDate) continue; // no delivery date on this item (e.g. an add-on), skip

    const deliveryDate = parseDeliveryDate(rawDate);
    if (!deliveryDate) continue; // couldn't parse — skip rather than wrongly remove

    const cutoffTime = getCutoffTimeForHandle(item.handle);

    if (isDeliveryDateExpired(deliveryDate, cutoffTime)) {
      keysToRemove.add(item.key);
      removedTitles.push(item.product_title);

      // Also remove any add-on linked to this item via _sg-master-id/_sg-addon-id
      const masterId = props["_sg-master-id"];
      if (masterId) {
        for (const addonItem of cart.items) {
          const addonProps = addonItem.properties || {};
          if (String(addonProps["_sg-addon-id"]) === String(masterId)) {
            keysToRemove.add(addonItem.key);
          }
        }
      }
    }
  }

  if (keysToRemove.size === 0) return;

  for (const key of keysToRemove) {
    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    } catch (e) {
      console.error("Cutoff check: failed to remove item", key, e);
    }
  }

  refreshCartUI();
}

function refreshCartUI() {
  // Ella theme's confirmed refresh trigger for the cart drawer
  const qtyInput = document.querySelector("div.has-items input.item-quantity");
  if (qtyInput) {
    qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // On the standalone cart page, a reload guarantees the removed item disappears
  if (window.location.pathname.includes("/cart")) {
    window.location.reload();
  }
}

// Run on cart page load
document.addEventListener("DOMContentLoaded", checkCartForExpiredDates);

// Run whenever the Ella cart drawer opens.
// Ella typically toggles an "active"/"open" class on the drawer container —
// this MutationObserver catches that without needing an exact event name.
const cartDrawerObserver = new MutationObserver(() => {
  const drawer = document.querySelector(
    '[id*="cart-drawer" i], [class*="cart-drawer" i], [class*="mini-cart" i], #CartDrawer'
  );
  if (drawer && (drawer.classList.contains("active") || drawer.classList.contains("open"))) {
    checkCartForExpiredDates();
  }
});
cartDrawerObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ["class"] });

// Also catch common cart-open events, in case Ella dispatches one of these
["cart:open", "cart-drawer:open", "drawer:open"].forEach((evt) => {
  document.addEventListener(evt, checkCartForExpiredDates);
});